<template>
  <div class="giftcard-add-view">
    <v-app-bar id="header" app color="primary" dense :class="['header', 'primaryText--text']">
      <v-row>
        <v-col cols="2" align-self="end">
          <v-btn text @click="navBack()">{{ $t("cancel") }}</v-btn>
        </v-col>

        <v-col cols="8" class="d-flex justify-center align-center">
          <div>
            <v-toolbar-title
              class="header-title1"
              v-if="$appMode == 0 && $store.state.store != null && $store.state.store.info != null"
              >{{ $store.getters.storeName }}</v-toolbar-title
            >
            <v-toolbar-title class="header-title2">{{ $t("giftcard") }}</v-toolbar-title>
          </div>
        </v-col>

        <v-col cols="2" align-self="end" style="text-align: right"> </v-col>
      </v-row>
    </v-app-bar>

    <v-form v-model="valid" ref="form">
      <v-container>
        <div class="text-h5">
          {{ $t("GIFTCARD.buy_giftcard") }}
        </div>
        <div class="text-h6 mt-2">
          {{ $t("GIFTCARD.select_giftcard_amount") }}
        </div>

        <div class="giftcard-amount-group">
          <v-btn
            class="gifcard-amount-item"
            :color="gca == giftcardAmount ? 'primary' : 'lightgray'"
            v-for="(gca, index) in giftcardAmounts"
            :key="index"
            :value="gca.amount"
            @click="SelectGiftCardAmount(gca)"
            >{{ gca.title }}</v-btn
          >
        </div>

        <v-radio-group v-model="recipient">
          <v-radio v-show="recipient == 'me'" :label="$t('GIFTCARD.for_my_own')" value="me"> </v-radio>
          <div v-show="recipient == 'me'" class="ml-11">
            <v-select
              v-model="giftcard"
              :items="giftcards"
              :label="$t('GIFTCARD.select_giftcard')"
              @click="recipient = 'me'"
            >
              <template v-slot:selection="data">
                <div v-if="data.item.newCard">{{ $t("GIFTCARD.new_card") }}</div>
                <div v-else class="giftcard">
                  <div class="float-left">{{ $t("GIFTCARD.cardCode") }} {{ data.item.CardCode }}</div>
                  <div class="float-right">{{ $t("GIFTCARD.balance") }} {{ data.item.CurBalance | currency }}</div>
                </div>
              </template>
              <template v-slot:item="data">
                <div v-if="data.item.newCard">{{ $t("GIFTCARD.new_card") }}</div>
                <div v-else class="giftcard">
                  <div class="float-left">{{ $t("GIFTCARD.cardCode") }} {{ data.item.CardCode }}</div>
                  <div class="float-right">{{ $t("GIFTCARD.balance") }} {{ data.item.CurBalance | currency }}</div>
                </div>
              </template>
            </v-select>
          </div>

          <v-radio v-show="recipient == 'someone'" :label="$t('GIFTCARD.for_friend')" value="someone"> </v-radio>
          <div v-show="recipient == 'someone'" class="ml-11">
            <v-text-field
              :label="$t('GIFTCARD.recipient_phone1')"
              dense
              filled
              v-model="recipientPhone1"
              :rules="inputRules.phone1"
              @click="recipient = 'someone'"
            ></v-text-field>

            <v-text-field
              :label="$t('GIFTCARD.recipient_phone2')"
              dense
              filled
              v-model="recipientPhone2"
              :rules="inputRules.phone2"
              message="test"
              class="mt-4"
              @click="recipient = 'someone'"
            ></v-text-field>

            <v-textarea :label="$t('message')" auto-grow filled v-model="recipientMessage" maxlength="100">
            </v-textarea>
          </div>
        </v-radio-group>

        <div v-show="stripeEnabled" class="card-form mt-3">
          <div class="accent--text font-weight-bold ">
            {{ $t("payment") }}
          </div>

          <v-radio-group v-model="stripe.cardSelected" class="card-group mt-0">
            <div class="d-flex" v-for="card in stripe.savedCards" :key="card.id">
              <v-radio :value="card.id" class="card-item">
                <template v-slot:label>
                  <div class="card-item-label">
                    {{ card.card }} Ending in {{ card.last4 }} Expiry: {{ card.exp_month }}/{{ card.exp_year }}
                  </div>
                </template>
              </v-radio>
              <v-icon right color="warning" class="mb-3" @click="removeCard(card.id)">
                mdi-delete
              </v-icon>
            </div>
            <v-radio value="new-card" class="card-item">
              <template v-slot:label>
                <div id="card-element" class="card-field"></div>
              </template>
            </v-radio>
          </v-radio-group>

          <v-switch
            color="secondary"
            class="stripe-save-card"
            v-model="stripe.saveCard"
            :ripple="false"
            dense
            label="Save card details"
          ></v-switch>

          <v-img class="stripe-powered-by" src="@/assets/imgs/powered-by-stripe.svg" width="120" x-height="50" />
        </div>

        <v-footer
          fixed
          :class="{
            '': $vuetify.breakpoint.mdAndUp,
          }"
        >
          <div v-if="payFailedMessage != ''" class="pay-failed-text">
            {{ payFailedMessage }}
          </div>

          <v-btn v-if="stripeEnabled" block tile :disabled="!paidValid" class="confirm-button" @click="pay">{{
            $t("GIFTCARD.pay")
          }}</v-btn>
        </v-footer>
      </v-container>
    </v-form>

    <v-overlay :value="refreshing">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>

    <PaymentTermsOfUseDialog ref="termsOfUseDialog"></PaymentTermsOfUseDialog>
  </div>
</template>

<script>
import PaymentTermsOfUseDialog from "@/dialogs/PaymentTermsOfUseDialog";
export default {
  components: {
    PaymentTermsOfUseDialog,
  },

  data() {
    return {
      refreshing: false,
      valid: false,

      inputRules: {
        phone1: [v => this.recipient == "me" || !v || this.$isValidPhoneNumber(v) || "Invalid phone number"],

        phone2: [
          v => this.recipient == "me" || !v || this.$isValidPhoneNumber(v) || "Invalid phone number",
          v => this.recipient == "me" || v == this.recipientPhone1 || "Phone number not matched",
        ],
      },

      giftcardAmounts: [
        { title: "$20", amount: 20, currency: "CAD" },
        { title: "$50", amount: 50, currency: "CAD" },
        { title: "$100", amount: 100, currency: "CAD" },
        { title: "$150", amount: 150, currency: "CAD" },
        { title: "$200", amount: 200, currency: "CAD" },
      ],
      giftcardAmount: null,
      recipient: null,
      recipientPhone1: null,
      recipientPhone2: null,
      recipientMessage: null,
      giftcard: null,

      stripe: {},
      payFailedMessage: "",
    };
  },

  computed: {
    stripeEnabled() {
      return this.$store.getters.memberStripeEnabled && this.stripe != null;
    },

    globalPaymentEnabled() {
      return this.$store.getters.globalPaymentEnabled;
    },

    paidValid() {
      console.log(this.valid, this.recipient, this.recipientPhone1, this.recipientPhone2);
      return (
        this.valid &&
        (this.recipient == "me" || (this.recipientPhone1 != null && this.recipientPhone1 == this.recipientPhone2))
      );
    },

    giftcards() {
      if (this.$store.state.giftcards.length == 0) return [{ newCard: true }];
      else return this.$store.state.giftcards;
    },
  },

  watch: {
    recipient() {
      this.$refs.form.validate();
    },

    async giftcardAmount(newValue) {
      if (newValue == null) return;

      this.refreshing = true;
      let payerId = await this.sp_getPayerId();
      this.stripe = await this.sp_init({
        amount: this.giftcardAmount.amount,
        currency: "CAD",
        payerId: payerId,
        withholding: 0,
        owner: this,
      });
      this.$set(this.stripe, "saveCard", this.sp_getSaveCard());
      console.log(this.stripe);

      this.refreshing = false;
    },
  },

  async activated() {
    console.log("giftcard-add activated");

    this.giftcardAmount = null;
    this.giftcardAmount = this.giftcardAmounts[0];
    this.recipient = this.$route.params.recipient != "someone" ? "me" : "someone";
    this.recipientPhone1 = null;
    this.recipientPhone2 = null;
    this.recipientMessage = null;

    this.dispatchAction("getGiftcards").then(resp => {
      if (resp) {
        this.giftcard = this.giftcards[0];
      }
    });
    /*
    this.rest_getMemberGiftcards(this.$store.state.member.MemberCode).then(resp => {
      if (resp.result == "ok" && resp.record != null) {
        this.giftCards = resp.record;
      } else {
        this.giftCards = [];
      }
      if (this.giftCards.length == 0) this.giftCards.push({ newCard: true });

      this.giftCard = this.giftCards[0];
    });
*/
  },

  methods: {
    SelectGiftCardAmount(gca) {
      this.giftcardAmount = gca;
    },

    removeCard(cardId) {
      this.$root.confirmDialog(this.$t("confirm"), "", this.$t("confirm_remove_card_question")).then(resp => {
        if (resp) {
          this.refreshing = true;
          this.sp_removeCard(cardId)
            .then(rpdata => {
              if (rpdata.result == "ok") {
                this.stripe.savedCards = this.stripe.savedCards.filter(value => value.id != cardId);
              }
              this.refreshing = false;
            })
            .catch(err => {
              this.refreshing = false;
              console.log(err);
            });
        }
      });
    },

    updateSaveCardStatus() {
      this.sp_setSaveCard(this.stripe.saveCard);
      if (!this.stripe.saveCard) this.sp_setPayerId("");
    },

    async pay(event) {
      event.preventDefault();

      let yourself = this.$t("yourself");
      let resp = await this.$root.confirmDialog(
        this.$t("confirm_payment_title"),
        "",
        this.$t("GIFTCARD.confirm_payment", {
          amount: this.giftcardAmount.amount,
          recipient: this.recipient == "me" ? yourself : this.recipientPhone1,
        })
      );
      if (resp) {
        if (this.stripeEnabled) {
          if (this.stripe.ready) {
            try {
              this.refreshing = true;
              this.stripe.confirmed = true;
              let paidResp = { newCard: false, result: null, orderId: null, resp: null };

              if (this.stripe.cardSelected == "new-card") {
                let obj = {
                  payment_method: { card: this.stripe.cardElement },
                  setup_future_usage: "off_session",
                };
                let resp = await this.stripe.res.confirmCardPayment(this.stripe.paymentKey, obj);
                let paymentIntentStatus = resp.paymentIntent != null ? resp.paymentIntent.status : "";
                paidResp.newCard = true;
                paidResp.result =
                  paymentIntentStatus == "succeeded" || paymentIntentStatus == "requires_capture"
                    ? "ok"
                    : paymentIntentStatus;
                paidResp.orderId = paidResp.result == "ok" ? resp.paymentIntent.id : "";
                paidResp.resp = resp;
              } else {
                let resp = await this.sp_payBySavedCard({
                  amount: this.giftcardAmount.amount,
                  currency: this.giftcardAmount.currency,
                  payerId: this.stripe.payerId,
                  cardId: this.stripe.cardSelected,
                });
                paidResp.newCard = false;
                paidResp.result = resp.result;
                paidResp.orderId = resp.result == "ok" ? resp.data.order_id : "";
                paidResp.resp = resp;
              }

              console.log(paidResp);

              if (paidResp.result == "ok") {
                let recipientPhoneId;
                let recipientMessage;
                let cardCode;
                let remark;
                if (this.recipient == "me") {
                  cardCode = this.giftcard == null || this.giftcard.newCard ? null : this.giftcard.CardCode;
                }
                if (this.recipient == "someone") {
                  remark = "Reload by " + this.$store.getters.memberName;
                  recipientMessage = this.recipientMessage;
                }

                if (this.recipient == "someone") recipientPhoneId = "+1" + this.recipientPhone1.replace(/\D+/g, "");

                let payload = {
                  recipientPhoneId: recipientPhoneId,
                  recipientMessage: recipientMessage,
                  cardCode: cardCode,
                  amount: this.giftcardAmount.amount,
                  remark: remark,
                };
                let reloadResp = await this.rest_reloadMemberGiftcard(payload);
                if (reloadResp.result == "ok") {
                  let completed = true;
                  if (this.stripe.preAuthorize) {
                    // stripe capture payment
                    await this.sp_capturePayment(paidResp.orderId);
                    completed = true;
                  }

                  if (completed) {
                    this.updateSaveCardStatus();
                    this.navBack();
                  } else {
                    //
                  }
                }
              } else {
                if (paidResp.result == null) throw new Error("Known error");
                else throw new Error(paidResp.result);
              }
            } catch (err) {
              this.$root.messageDialog(this.$t("error_alert_title"), "", err);
              console.log(err);
            } finally {
              this.refreshing = false;
              this.stripe.confirmed = false;
            }
          }
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/sass/payment.scss";

.container {
  max-width: 600px;
  margin-bottom: 200px;
}

.giftcard-amount-group > .v-btn {
  //background-color: lightgray !important;
  min-width: 100px !important;
  min-height: 50px !important;
  padding: 8px;
  margin: 8px !important;
}

.giftcard {
  width: 100%;
}

.v-footer {
  padding: 0px;
  padding-bottom: calc(env(safe-area-inset-bottom));

  > div {
    width: 100%;
  }

  .pay-failed-text {
    //white-space: pre-line;
    padding: 12px;
    margin-bottom: 2px;
    font-size: 0.9em;
    text-align: center;
    background-color: var(--v-warning-base);
    color: var(--v-warningText-base);
  }

  .confirm-button {
    width: 100%;
    height: 36px;
    background-color: var(--v-info-base) !important;
    color: var(--v-infoText-base) !important;
  }
}
</style>
